import {
    TokenListMetadata,
    getVaultInitTimelockTransactions,
    getVaultDepositTransaction,
    getVaultWithdrawTransaction,
    VaultDepositParams,
    VaultWithdrawParams,
    InitVaultTimelockTxnParams,
    CancelVaultTimeLockTxnParams,
    getVaultCancelTimelockTransaction,
    getVaultClaimFeesTransaction,
    ClaimVaultFeesTxnParams,
    getVaultUpdateDepositEnabledTransaction,
    UpdateLendingVaultDepositsTxnParams,
    CreateRewardsTxnParams,
    getVaultCreateRewardsTransaction,
    UpdateRewardsScheduleTxnParams,
    getVaultEditRewardsTransaction,
    ClaimLendVaultRewardsParams,
    getVaultClaimExternalRewardsTransactions
} from "@bridgesplit/abf-sdk";
import { RoundingType, uiAmountToLamports, AnalyticsTransactionType, VaultAnalyticsInfo } from "@bridgesplit/utils";

import { useAbfFetches } from "../reducers";
import { AbfGeneratorResult, AbfTransactionDetails } from "../types";
import { useAbfGenerateTransaction, useAbfGenerateTransactionWithData, wrapAsAbfGeneratorResult } from "./common";
import { useCreateSimulationPromise } from "./simulation";
import { useUserTokens } from "../api";

function useVaultEscrowRefetch() {
    const { resetNapoleonApi, resetLendingVaultPublicApi, resetMarketsPublicApi, resetLendingVaultPositionsApi } =
        useAbfFetches();
    const { refresh } = useUserTokens();
    return () => {
        resetNapoleonApi();
        void refresh();
        resetLendingVaultPublicApi();
        resetMarketsPublicApi();
        resetLendingVaultPositionsApi();
    };
}

type DepositParams = VaultDepositParams & {
    principalMetadata: TokenListMetadata;
    userWallet: string;
    newDeposit: boolean;
};

export function useVaultDepositTransaction(): AbfTransactionDetails<DepositParams> {
    const generate = useAbfGenerateTransactionWithData();
    const refetch = useVaultEscrowRefetch();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams({
        principalMetadata,
        userWallet,
        vault,
        newDeposit,
        ...params
    }: DepositParams): AbfGeneratorResult {
        const principalAmount = uiAmountToLamports(
            params.principalAmount,
            principalMetadata.decimals,
            RoundingType.ROUND
        );
        const minLpAmount = uiAmountToLamports(params.minLpAmount, principalMetadata.decimals, RoundingType.DOWN);
        const analyticsInfo: VaultAnalyticsInfo = {
            transactionType: AnalyticsTransactionType.VaultDeposit,
            vault,
            principalAmount,
            minLpAmount,
            depositOnly: newDeposit
        };
        return wrapAsAbfGeneratorResult(
            generate({
                generateFunction: getVaultDepositTransaction,
                identifier: "Deposit",
                params: { ...params, principalAmount, minLpAmount, vault },
                analyticsInfo
            }),
            createSimulationPromise,
            { order: "sequential" },
            analyticsInfo
        );
    }

    const sendOptions = { refetch };

    return { getTransactionsWithParams, sendOptions, description: "Depositing" };
}

type WithdrawParams = VaultWithdrawParams & {
    principalMetadata: TokenListMetadata;
    userWallet: string;
    withdrawAll: boolean;
};

export function useVaultWithdrawTransaction(): AbfTransactionDetails<WithdrawParams> {
    const generate = useAbfGenerateTransactionWithData();
    const refetch = useVaultEscrowRefetch();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams({
        principalMetadata,
        userWallet,
        withdrawAll,
        vault,
        ...params
    }: WithdrawParams): AbfGeneratorResult {
        const amountPrincipal = uiAmountToLamports(
            params.amountPrincipal,
            principalMetadata.decimals,
            RoundingType.DOWN
        );
        const maxAmountLp = uiAmountToLamports(params.maxAmountLp, principalMetadata.decimals, RoundingType.UP);
        const analyticsInfo: VaultAnalyticsInfo = {
            transactionType: AnalyticsTransactionType.VaultWithdraw,
            vault,
            amountPrincipal,
            maxAmountLp,
            withdrawAll
        };
        return wrapAsAbfGeneratorResult(
            generate({
                generateFunction: getVaultWithdrawTransaction,
                identifier: "Withdraw",
                params: { ...params, amountPrincipal, maxAmountLp, vault, withdrawAll },
                analyticsInfo
            }),
            createSimulationPromise,
            { order: "sequential" },
            analyticsInfo
        );
    }

    const sendOptions = { refetch };

    return { getTransactionsWithParams, sendOptions, description: "Withdrawing" };
}

export function useVaultTimelockInitTransactions(): AbfTransactionDetails<InitVaultTimelockTxnParams> {
    const generate = useAbfGenerateTransaction();
    const refetch = useVaultEscrowRefetch();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: InitVaultTimelockTxnParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: getVaultInitTimelockTransactions, identifier: "Create timelock", params }),
            createSimulationPromise,
            { order: "parallel" }
        );
    }

    const sendOptions = { refetch };

    return { getTransactionsWithParams, sendOptions, description: "Creating timelock" };
}

export function useVaultTimelockCancelTransaction(): AbfTransactionDetails<CancelVaultTimeLockTxnParams[]> {
    const generate = useAbfGenerateTransaction();
    const refetch = useVaultEscrowRefetch();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: CancelVaultTimeLockTxnParams[]): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: getVaultCancelTimelockTransaction, identifier: "Cancel timelock", params }),
            createSimulationPromise,
            { order: "parallel" }
        );
    }

    const sendOptions = { refetch };

    return { getTransactionsWithParams, sendOptions, description: "Cancel timelock" };
}

export function useVaultClaimFeeTransaction(): AbfTransactionDetails<ClaimVaultFeesTxnParams> {
    const generate = useAbfGenerateTransaction();
    const refetch = useVaultEscrowRefetch();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: ClaimVaultFeesTxnParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: getVaultClaimFeesTransaction, identifier: "Claim vault fee", params }),
            createSimulationPromise,
            { order: "sequential" }
        );
    }

    const sendOptions = { refetch };

    return { getTransactionsWithParams, sendOptions, description: "Claiming vault fee" };
}

export function useVaultUpdateDepositsEnabledTransaction(): AbfTransactionDetails<UpdateLendingVaultDepositsTxnParams> {
    const generate = useAbfGenerateTransaction();
    const refetch = useVaultEscrowRefetch();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: UpdateLendingVaultDepositsTxnParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({
                generateFunction: getVaultUpdateDepositEnabledTransaction,
                identifier: "Update vault deposits",
                params
            }),
            createSimulationPromise,
            { order: "sequential" }
        );
    }

    const sendOptions = { refetch };

    return { getTransactionsWithParams, sendOptions, description: "Updating vault deposits" };
}

export function useVaultCreateRewardsTransaction(): AbfTransactionDetails<CreateRewardsTxnParams> {
    const generate = useAbfGenerateTransaction();
    const refetch = useVaultEscrowRefetch();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: CreateRewardsTxnParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: getVaultCreateRewardsTransaction, identifier: "Create rewards", params }),
            createSimulationPromise,
            { order: "sequential" }
        );
    }

    const sendOptions = { refetch };

    return { getTransactionsWithParams, sendOptions, description: "Create vault rewards" };
}

export function useVaultEditRewardsTransaction(): AbfTransactionDetails<UpdateRewardsScheduleTxnParams> {
    const generate = useAbfGenerateTransaction();
    const refetch = useVaultEscrowRefetch();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: UpdateRewardsScheduleTxnParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: getVaultEditRewardsTransaction, identifier: "Edit rewards", params }),
            createSimulationPromise,
            { order: "sequential" }
        );
    }

    const sendOptions = { refetch };

    return { getTransactionsWithParams, sendOptions, description: "Edit vault reward" };
}

export function useVaultClaimExternalRewardsTransactions(): AbfTransactionDetails<ClaimLendVaultRewardsParams> {
    const generate = useAbfGenerateTransaction();
    const refetch = useVaultEscrowRefetch();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: ClaimLendVaultRewardsParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({
                generateFunction: getVaultClaimExternalRewardsTransactions,
                identifier: "Claim external rewards",
                params
            }),
            createSimulationPromise,
            { order: "parallel" }
        );
    }

    const sendOptions = { refetch };

    return { getTransactionsWithParams, sendOptions, description: "Claim external rewards" };
}
